import { LayerDefinition } from './types';

export const alpineWildflowersDefinition: LayerDefinition = {
  id: 'alpine_wildflowers',
  name: 'Alpine & Mountain Wildflowers',
  category: 'environment',
  categoryLabel: 'Seasonal & Phenology',
  icon: '🌸',
  description: `Predicts bloom onset, peak floral concurrency, bloom duration, and display intensity (Mountain Bloom Intensity Index, MBII) for alpine and subalpine herbaceous perennials.

Driven by winter snowpack insulation, dynamic snowmelt biofix (SWE <= 0), and post-melt growing degree days (GDD) with elevation lapse-rate corrections.

Includes multi-species coverage (Arrowleaf Balsamroot, Subalpine Lupine, Indian Paintbrush, Avalanche Lily), dual domain support (North America 2.5km and Global Alpine 13km), and interactive summer calendar and multi-species concurrency curves.`,
  scoreDescription: `• Snowbound / Pre-Melt (Icy Cyan): Terrain still insulated under winter snowpack.
• Emerging / Vegetative (Fresh Green): Post-melt vegetative green-up and bud initiation.
• Early Bloom (Lime Yellow): Initial floral bud opening (15-30% open).
• Peak Concurrency (Magenta / Rose): Broadfield meadow bloom display (60-100% open).
• Past Peak (Amber / Orange): Seed maturation and petal drop.
• MBII Intensity (Gold to Hot Pink): Mountain Bloom Intensity factoring floral synchrony, insulation, frost risk, and snowmelt sub-irrigation.`,
  sourceModels: ['nbm', 'icon13', 'gdps'],
  outputModels: ['Alpine Wildflowers (.pmtiles)'],
  config: {
    defaultModel: 'seasonal'
  }
};

export const desertWildflowersDefinition: LayerDefinition = {
  id: 'desert_wildflowers',
  name: 'Desert Superbloom & Annuals',
  category: 'environment',
  categoryLabel: 'Seasonal & Phenology',
  icon: '🏜️',
  description: `Predicts episodic germination probability, storm cadence viability, seasonal precipitation anomalies, and flowering intensity (Bloom Intensity Index, BII) across arid basins and alluvial fans.

Models the 4-phase environmental cascade: autumn seed-leaching storm trigger (>= 20mm), mid-winter storm spacing (<= 18 days), spring thermal maturation, and desert heat scorch resistance.`,
  scoreDescription: `• Germination / Rosette (Meadow Green): Early basal leaves emerging following soaking rainfall.
• Early Blooms (Lime Yellow): Initial desert wash and alluvial fan flowering.
• Peak Floral Carpet (Vibrant Rose / Crimson): Peak carpet concurrency across desert valley floors.
• Past Peak (Warm Gold): Seed setting and rapid thermal desiccation.
• BII Score (0-100): Desert Bloom Intensity measuring superbloom probability and display density.`,
  sourceModels: ['nbm', 'icon13'],
  outputModels: ['Desert Superbloom (.pmtiles)'],
  config: {
    defaultModel: 'seasonal'
  }
};

export const wildBerriesDefinition: LayerDefinition = {
  id: 'wild_berries',
  name: 'Wild Berry Ripening & Foraging',
  category: 'environment',
  categoryLabel: 'Seasonal & Phenology',
  icon: '🫐',
  description: `Predicts two-phase developmental progression (anthesis and fruit maturation), harvest windows, ascending mountain elevation-wave timing, and crop stress badges for native edible species without exposing sensitive GPS locations.

Target species include Mountain Huckleberry (Vaccinium membranaceum), Salmonberry (Rubus spectabilis), and Wild Blackberries. Evaluates crop quality badges (Prime/Plump, Moderate/Fair, Drought-Stressed/Seedy, Frost Aborted).`,
  scoreDescription: `• Green / Immature (Meadow Green): Developing unripe green fruit.
• Early Ripe Window (Magenta Violet): Appearance of first ripe berries for early foragers.
• Peak Sugar Harvest (Deep Royal Purple): Prime sweet flavor, high brix concurrency.
• Overripe (Russet Brown): Fruit drop, shriveling, and post-season senescence.
• Crop Quality Badges: Emerald (Prime / Plump), Amber (Moderate), Orange (Drought-Stressed), Ruby (Frost Aborted).`,
  sourceModels: ['nbm', 'icon13', 'gdps'],
  outputModels: ['Wild Berries (.pmtiles)'],
  config: {
    defaultModel: 'seasonal'
  }
};

export default alpineWildflowersDefinition;
